/**
 * Agent Provider Setup (React-Only Version)
 *
 * 🔥 CONVERTED FROM MobX TO PURE REACT!
 * No more MobX, no more observer() needed - guaranteed React reactivity!
 */

import AgentSessionProvider from './context/AgentSessionProvider';
import AgentErrorBoundary from './error/AgentErrorBoundary';

/**
 * Agent Provider with Pure React Architecture
 * Uses useReducer + Context instead of MobX for guaranteed reactivity
 *
 * Configuration is driven by environment variables:
 * - VITE_AGENT_ADAPTER: 'mock' | 'agentcore' | 'local-server'
 * - VITE_AGENTCORE_REGION: AWS region for AgentCore
 * - VITE_AGENTCORE_AGENT_ARN: AgentCore runtime ARN
 * - VITE_AGENTCORE_QUALIFIER: AgentCore qualifier (usually 'DEFAULT')
 */
// eslint-disable-next-line react/prop-types
const AgentProvider = ({ children }) => {
  if (import.meta.env.DEV) console.log('🏭 AGENT PROVIDER: Using converted React-only provider (no MobX!)');

  // Create adapter configuration from environment variables
  const adapterConfig = {
    type: import.meta.env.VITE_AGENT_ADAPTER || 'mock',

    mockConfig: {
      responseDelay: 1000,
      enableToolExecution: true,
      simulateErrors: false,
      toolExecutionDuration: 45000,
      realisticMode: false
    },

    agentCoreConfig: {
      region: import.meta.env.VITE_AGENTCORE_REGION || 'eu-central-1',
      agentRuntimeArn: import.meta.env.VITE_AGENTCORE_AGENT_ARN || 'arn:aws:bedrock-agentcore:eu-central-1:813332623731:runtime/td_agent-t1Obr73Khx',
      qualifier: import.meta.env.VITE_AGENTCORE_QUALIFIER || 'DEFAULT',
      timeout: 60000,
      getCredentials: () => {
        // Access AWS Cognito credentials from existing auth system
        return window.AWS?.config?.credentials || null;
      }
    }
  };

  if (import.meta.env.DEV) console.log('🎯 AGENT PROVIDER: Using adapter configuration:', {
    type: adapterConfig.type,
    agentRuntimeArn: adapterConfig.agentCoreConfig?.agentRuntimeArn?.substring(0, 50) + '...'
  });

  return (
    <AgentErrorBoundary>
      <AgentSessionProvider adapterConfig={adapterConfig}>
        {children}
      </AgentSessionProvider>
    </AgentErrorBoundary>
  );
};

export default AgentProvider;

/**
 * Recommended usage - Wrap AgentLayout (most efficient):
 *
 * // src/components/agent/layout/AgentLayout.jsx
 * import AgentProvider from '../AgentProvider';
 *
 * const AgentLayout = ({ children, ...props }) => {
 *   return (
 *     <AgentProvider>
 *       <div className="agent-layout">
 *         {children}
 *       </div>
 *     </AgentProvider>
 *   );
 * };
 *
 * Alternative - Route-level wrapping:
 *
 * // src/App.jsx
 * <Route path="/en/agent/*" element={
 *   <AgentProvider>
 *     <Routes>
 *       <Route path="/" element={<AgentWelcomePage />} />
 *       <Route path="/chat/:chatId" element={<AgentChatPage />} />
 *     </Routes>
 *   </AgentProvider>
 * } />
 *
 * Benefits of scoped approach:
 * - Only loads when agent features are used
 * - Better performance (no unnecessary context/state)
 * - Cleaner separation of concerns
 * - Easier testing and bundle splitting
 *
 * Environment Variables:
 * - VITE_AGENT_ADAPTER=mock|agentcore|local-server
 * - VITE_AGENTCORE_REGION=eu-central-1
 * - VITE_AGENTCORE_AGENT_ARN=arn:aws:bedrock-agentcore:eu-central-1:813332623731:runtime/td_agent-t1Obr73Khx
 * - VITE_AGENTCORE_QUALIFIER=DEFAULT
 * - VITE_LOCAL_AGENT_URL=http://localhost:3001
 *
 * To switch from mock to real AgentCore:
 * 1. Set VITE_AGENT_ADAPTER=agentcore in .env.local
 * 2. Ensure AWS Cognito credentials are configured
 * 3. Restart development server
 */